import React, { useCallback, useMemo, useState } from 'react';
import { Check, ChevronDown } from 'lucide-react';

import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { cn } from '@/lib/utils';

export interface SearchableSelectOption {
  value: string;
  label: string;
}

export interface SearchableSelectProps {
  value: string;
  onValueChange: (value: string) => void;
  options: SearchableSelectOption[];
  placeholder?: string;
  searchPlaceholder?: string;
  emptyText?: string;
  disabled?: boolean;
  triggerClassName?: string;
  itemClassName?: string;
}

/** Matches `SelectTrigger` from `@/components/ui/select`. */
const selectTriggerClassName =
  'flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 [&>span]:line-clamp-1';

/** Matches `SelectContent` from `@/components/ui/select`. */
const selectContentClassName =
  'relative z-[10050] max-h-96 min-w-[8rem] overflow-hidden rounded-md border bg-popover p-0 text-popover-foreground shadow-md data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1';

/** Matches `SelectItem` from `@/components/ui/select`. */
const selectItemClassName =
  'relative cursor-pointer select-none rounded-sm py-1.5 pl-8 pr-2 text-sm';

export const SearchableSelect: React.FC<SearchableSelectProps> = ({
  value,
  onValueChange,
  options,
  placeholder = 'Select an option',
  searchPlaceholder = 'Search...',
  emptyText = 'No results found.',
  disabled = false,
  triggerClassName,
  itemClassName,
}) => {
  const [open, setOpen] = useState(false);

  const selectedLabel = useMemo(
    () => options.find((option) => option.value === value)?.label,
    [options, value],
  );

  const handleSelectOption = useCallback(
    (nextValue: string) => {
      onValueChange(nextValue);
      setOpen(false);
    },
    [onValueChange],
  );

  const filterOptions = useCallback(
    (itemValue: string, search: string) => {
      const normalizedSearch = search.trim().toLowerCase();
      if (!normalizedSearch) return 1;

      const option = options.find((entry) => entry.value === itemValue);
      if (!option) return 0;

      const haystack = `${option.label} ${option.value}`.toLowerCase();
      return haystack.includes(normalizedSearch) ? 1 : 0;
    },
    [options],
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn(
            selectTriggerClassName,
            !selectedLabel && 'text-muted-foreground',
            triggerClassName,
          )}
        >
          <span className="pointer-events-none block min-w-0 max-w-[calc(100%-1.5rem)] truncate text-left">
            {selectedLabel ?? placeholder}
          </span>
          <ChevronDown className="h-4 w-4 shrink-0 opacity-50" aria-hidden />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={4}
        className={cn(
          selectContentClassName,
          'w-[var(--radix-popover-trigger-width)]',
        )}
        onOpenAutoFocus={(event) => event.preventDefault()}
      >
        <Command
          key={open ? 'open' : 'closed'}
          className="bg-popover"
          filter={filterOptions}
        >
          <CommandInput placeholder={searchPlaceholder} className="h-9" />
          <CommandEmpty className="py-6 text-center text-sm">{emptyText}</CommandEmpty>
          <CommandList className="max-h-96">
            <CommandGroup className="p-1">
              {options.map((option) => (
                <CommandItem
                  key={option.value}
                  value={option.value}
                  keywords={[option.label]}
                  onMouseDown={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                  }}
                  onClick={() => handleSelectOption(option.value)}
                  onSelect={() => handleSelectOption(option.value)}
                  className={cn(
                    'aria-selected:bg-accent aria-selected:text-accent-foreground',
                    selectItemClassName,
                    itemClassName,
                  )}
                >
                  <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
                    <Check
                      className={cn(
                        'h-4 w-4',
                        value === option.value ? 'opacity-100' : 'opacity-0',
                      )}
                    />
                  </span>
                  <span className="truncate">{option.label}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
